import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { setServio } from "../../../../../../redux/servioSlice";
import { RootState } from "../../../../../../redux/store";
import { DEFAULT_MAIN_COLOR } from "@constants/index";

import { Container, SectionTitle } from "./styled";
import { FiltersCard } from "./FiltersCard";
import { SortCard } from "./SortCard";
import {
    CommandesState,
    FilterKey,
    FilterConfig,
    SortKey,
    DEFAULT_FILTERS,
    DEFAULT_SORT_OPTIONS,
} from "./types";

export function Commandes(): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor =
        useSelector((s: RootState) => s.global.infoUser.maincolor) ||
        DEFAULT_MAIN_COLOR;

    const reduxCommandes = useSelector(
        (s: RootState) => s.servio.commandes
    ) as Partial<CommandesState>;

    // Redux holds the source of truth; fill any missing field with the
    // module default so the card props are always fully populated.
    const state: CommandesState = React.useMemo(
        () => ({
            filters: { ...DEFAULT_FILTERS, ...(reduxCommandes.filters || {}) },
            sortOptions: {
                ...DEFAULT_SORT_OPTIONS,
                ...(reduxCommandes.sortOptions || {}),
            },
        }),
        [reduxCommandes]
    );

    const [activeColorPicker, setActiveColorPicker] = React.useState<
        string | null
    >(null);

    const updateFilter = (
        key: FilterKey,
        updates: Partial<FilterConfig>
    ): void => {
        dispatch(
            setServio({
                commandes: {
                    ...state,
                    filters: {
                        ...state.filters,
                        [key]: { ...state.filters[key], ...updates },
                    },
                },
            })
        );
    };

    const toggleSortOption = (key: SortKey): void => {
        dispatch(
            setServio({
                commandes: {
                    ...state,
                    sortOptions: {
                        ...state.sortOptions,
                        [key]: !state.sortOptions[key],
                    },
                },
            })
        );
    };

    const resetFilters = (): void => {
        dispatch(
            setServio({
                commandes: { ...state, filters: { ...DEFAULT_FILTERS } },
            })
        );
    };

    const resetSort = (): void => {
        dispatch(
            setServio({
                commandes: {
                    ...state,
                    sortOptions: { ...DEFAULT_SORT_OPTIONS },
                },
            })
        );
    };

    return (
        <Container data-testid="servio-commandes-tab">
            <div>
                <SectionTitle>{t("Orders")}</SectionTitle>
            </div>

            <FiltersCard
                filters={state.filters}
                mainColor={mainColor}
                activeColorPicker={activeColorPicker}
                setActiveColorPicker={setActiveColorPicker}
                onUpdateFilter={updateFilter}
                onReset={resetFilters}
            />

            <SortCard
                sortOptions={state.sortOptions}
                mainColor={mainColor}
                onToggle={toggleSortOption}
                onReset={resetSort}
            />
        </Container>
    );
}
